/**
 * Whop Pixel Utility
 * Handles initialization and page tracking for Whop analytics.
 */

import { config } from '../config/env'
import { logger } from './logger'

const WHOP_SCRIPT_URL = 'https://t.whop.tw/s.js'
const WHOP_PIXEL_READY_TIMEOUT_MS = 10000
const WHOP_FINGERPRINT_WAIT_MS = 5000

let whopPixelReadyPromise = null
let whopPixelReadyResolved = false
let resolveWhopPixelReady = null

/** Captured from Whop's own POST to t.whop.tw/conversions */
let capturedFingerprint = null
let capturedFingerprintConfidence = null
let fingerprintWaiters = []

const notifyFingerprintWaiters = () => {
  const waiters = fingerprintWaiters
  fingerprintWaiters = []
  waiters.forEach((resolve) => resolve(true))
}

const cacheFingerprintFromPayload = (payload) => {
  const fp = payload?.context?.fingerprint
  if (!fp) return

  capturedFingerprint = fp
  if (payload.context.fingerprint_confidence != null) {
    capturedFingerprintConfidence = payload.context.fingerprint_confidence
  }
  logger.debug('📊 Captured Whop fingerprint from /conversions:', capturedFingerprint)
  notifyFingerprintWaiters()
}

/**
 * Intercept Whop browser /conversions requests so we can reuse the same
 * fingerprint + confidence values in our trigger-whop-event payloads.
 * Must be installed before s.js starts posting.
 */
const installWhopConversionsInterceptor = () => {
  if (typeof window === 'undefined' || window.__whopConversionsIntercepted) return
  window.__whopConversionsIntercepted = true

  const originalFetch = window.fetch.bind(window)
  window.fetch = async (...args) => {
    try {
      const input = args[0]
      const init = args[1] || {}
      const url =
        typeof input === 'string'
          ? input
          : input && typeof input.url === 'string'
            ? input.url
            : ''

      if (url.includes('t.whop.tw/conversions') && init.body) {
        const raw = typeof init.body === 'string' ? init.body : null
        if (raw) {
          try {
            cacheFingerprintFromPayload(JSON.parse(raw))
          } catch (_) {
            // ignore non-JSON bodies
          }
        }
      }
    } catch (_) {
      // never break Whop's own fetch
    }

    return originalFetch(...args)
  }
}

/**
 * Fingerprint values captured from Whop pixel /conversions calls.
 * @returns {{ fingerprint: string|null, fingerprint_confidence: number|null }}
 */
export const getCapturedWhopFingerprint = () => ({
  fingerprint: capturedFingerprint,
  fingerprint_confidence: capturedFingerprintConfidence,
})

/**
 * Wait until Whop's page /conversions request has produced a fingerprint
 * (or timeout). Call after whenWhopPixelReady().
 * @returns {Promise<boolean>}
 */
export const whenWhopFingerprintReady = () => {
  if (capturedFingerprint) return Promise.resolve(true)

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      fingerprintWaiters = fingerprintWaiters.filter((w) => w !== onReady)
      if (!capturedFingerprint) {
        logger.warn('⚠️ Whop fingerprint not captured yet — proceeding without it')
      }
      resolve(!!capturedFingerprint)
    }, WHOP_FINGERPRINT_WAIT_MS)

    const onReady = () => {
      clearTimeout(timer)
      resolve(true)
    }
    fingerprintWaiters.push(onReady)
  })
}

const ensureReadyPromise = () => {
  if (!whopPixelReadyPromise) {
    whopPixelReadyPromise = new Promise((resolve) => {
      resolveWhopPixelReady = resolve
    })
  }
  return whopPixelReadyPromise
}

const markWhopPixelReady = () => {
  if (whopPixelReadyResolved) return
  whopPixelReadyResolved = true
  ensureReadyPromise()
  if (resolveWhopPixelReady) {
    resolveWhopPixelReady(true)
    resolveWhopPixelReady = null
  }
  logger.debug('✅ Whop Pixel script ready')
}

/**
 * Create the Whop stub queue (same as vendor snippet) before s.js loads.
 */
const ensureWhopStub = () => {
  if (window.whop) return window.whop

  const a = (window.whop = {
    q: [],
    t: +new Date(),
    s: [],
    o: 'https://t.whop.tw',
    track: function () {
      a.q.push([+new Date()].concat([].slice.call(arguments)))
    },
    setScope: function () {
      a.s = [].slice.call(arguments).filter(function (x) {
        return typeof x === 'string'
      })
      a.q.push([+new Date(), 'setScope'].concat(a.s))
    },
    scope: function () {
      const c = [].slice.call(arguments)
      return {
        track: function () {
          a.q.push(
            [+new Date()]
              .concat([].slice.call(arguments))
              .concat([{ __scope: c }])
          )
        },
      }
    },
  })

  return a
}

/**
 * Wait until the Whop pixel s.js has finished loading (or timeout / skip).
 * Safe to call before or after initializeWhopPixel.
 * @returns {Promise<boolean>} true if pixel loaded, false if skipped/timed out
 */
export const whenWhopPixelReady = () => {
  if (whopPixelReadyResolved) {
    return Promise.resolve(true)
  }

  const businessId = config.whopBusinessId
  if (!businessId || businessId === 'biz_xxxxxxxxxxxxx' || !businessId.startsWith('biz_')) {
    // No pixel will load — do not block trigger-whop-event
    return Promise.resolve(false)
  }

  // Kick off load if an event fires before App's initializeWhopPixel()
  if (!document.querySelector(`script[src="${WHOP_SCRIPT_URL}"]`) && !window.whop) {
    initializeWhopPixel()
  }

  const ready = ensureReadyPromise()

  // Safety net so funnel flow never hangs if s.js fails/hangs
  return Promise.race([
    ready,
    new Promise((resolve) => {
      setTimeout(() => {
        if (!whopPixelReadyResolved) {
          logger.warn('⚠️ Whop Pixel load timeout — proceeding with trigger-whop-event')
          markWhopPixelReady()
        }
        resolve(whopPixelReadyResolved)
      }, WHOP_PIXEL_READY_TIMEOUT_MS)
    }),
  ])
}

/**
 * Initialize the Whop tracking script, set business scope, and fire a page view.
 * Safe to call multiple times — guards against double-init.
 */
export const initializeWhopPixel = () => {
  const businessId = config.whopBusinessId

  if (!businessId || businessId === 'biz_xxxxxxxxxxxxx' || !businessId.startsWith('biz_')) {
    logger.debug('ℹ️ Whop Pixel not initialized: Set VITE_WHOP_BUSINESS_ID to a real biz_ ID')
    markWhopPixelReady()
    return
  }

  // Capture fingerprint from Whop's own conversions POSTs
  installWhopConversionsInterceptor()
  ensureReadyPromise()

  const existingScript = document.querySelector(`script[src="${WHOP_SCRIPT_URL}"]`)
  if (existingScript) {
    if (whopPixelReadyResolved || existingScript.dataset.whopLoaded === 'true') {
      markWhopPixelReady()
      return
    }
    // Script already injecting — wait for its load handlers
    logger.debug('ℹ️ Whop Pixel script already injecting')
    return
  }

  if (window.whop && window.whop._loaded) {
    markWhopPixelReady()
    return
  }

  logger.debug('📊 Initializing Whop Pixel:', businessId)

  try {
    ensureWhopStub()

    const script = document.createElement('script')
    script.async = true
    script.src = WHOP_SCRIPT_URL
    script.onload = () => {
      script.dataset.whopLoaded = 'true'
      if (window.whop) window.whop._loaded = true
      markWhopPixelReady()
    }
    script.onerror = () => {
      logger.error('❌ Whop Pixel script failed to load')
      // Unblock API queue even on failure
      markWhopPixelReady()
    }

    const firstScript = document.getElementsByTagName('script')[0]
    firstScript.parentNode.insertBefore(script, firstScript)

    window.whop.setScope(businessId)
    window.whop.track('page')

    logger.debug('📊 Whop Pixel stub ready; waiting for s.js')
  } catch (error) {
    logger.error('❌ Error initializing Whop Pixel:', error)
    markWhopPixelReady()
  }
}

/**
 * Fire a custom Whop track event (after initializeWhopPixel).
 * @param {string} eventName - Event name to track
 * @param {...*} args - Optional extra arguments passed to whop.track
 */
export const trackWhopEvent = (eventName, ...args) => {
  if (!window.whop) {
    logger.warn('⚠️ Whop Pixel not initialized. Event not tracked:', eventName)
    return
  }

  try {
    window.whop.track(eventName, ...args)
    logger.debug('✅ Whop event tracked:', eventName)
  } catch (error) {
    logger.error('❌ Error tracking Whop event:', error)
  }
}

export default {
  initializeWhopPixel,
  whenWhopPixelReady,
  whenWhopFingerprintReady,
  getCapturedWhopFingerprint,
  trackWhopEvent,
}
